import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMyPieces, useConfirmReceipt, useSubmitPiece } from '@/hooks/useOperations';
import { useAuthStore } from '@/stores/auth';
import { useNavigate } from 'react-router-dom';
import { Check, LogOut, AlertTriangle } from 'lucide-react';

export default function WorkerHome() {
  const { t, i18n } = useTranslation();
  const { data: pieces, isLoading } = useMyPieces();
  const confirmMutation = useConfirmReceipt();
  const submitMutation = useSubmitPiece();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [receivedWeight, setReceivedWeight] = useState('');
  const [finishedWeight, setFinishedWeight] = useState('');
  const [shape, setShape] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    document.title = 'My Pieces — Aditi Diamonds Vault';
  }, []);

  // Persist language preference to localStorage
  useEffect(() => {
    const savedLang = localStorage.getItem('aditi-lang');
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  const selected = pieces?.find((p: any) => p.id === selectedId);

  const toggleLang = () => {
    const newLang = i18n.language === 'en' ? 'ta' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('aditi-lang', newLang);
  };

  const handleConfirm = async () => {
    if (!selectedId || !receivedWeight) return;
    try {
      await confirmMutation.mutateAsync({
        pieceId: selectedId,
        receivedWeightCt: parseFloat(receivedWeight),
      });
      setSuccess(t('worker.confirmSuccess'));
      setReceivedWeight('');
      setSelectedId(null);
    } catch { /* handled */ }
  };

  const handleSubmit = async () => {
    if (!selectedId || !finishedWeight) return;
    try {
      await submitMutation.mutateAsync({
        pieceId: selectedId,
        postPolishWeightCt: parseFloat(finishedWeight),
        shape,
      });
      setSuccess(t('worker.submitSuccess'));
      setFinishedWeight('');
      setShape('');
      setSelectedId(null);
      setShowConfirmation(false);
    } catch { /* handled */ }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const statusColor = (status: string) => {
    if (status === 'awaiting_confirmation') return 'bg-orange-500/10 text-orange-400';
    if (status === 'in_progress') return 'bg-blue-500/10 text-blue-400';
    return 'bg-success/10 text-success';
  };

  const statusLabel = (status: string) => {
    if (status === 'awaiting_confirmation') return t('worker.awaitingConfirmation');
    if (status === 'in_progress') return t('worker.inProgress');
    return t('worker.submitted');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] px-4 pb-8 pt-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gold">{t('worker.myPieces')}</h1>
          <p className="text-sm text-muted-foreground">{user?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground"
          >
            {i18n.language === 'en' ? 'தமிழ்' : 'English'}
          </button>
          <button onClick={handleLogout} className="text-muted-foreground"><LogOut size={18} /></button>
        </div>
      </div>

      {success && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-4 py-3 text-sm text-success">
          <Check size={16} /> {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-xs underline">{t('common.cancel')}</button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-card" />)}
        </div>
      ) : selectedId && selected ? (
        // Detail view
        <div className="space-y-4">
          <button onClick={() => { setSelectedId(null); setReceivedWeight(''); setFinishedWeight(''); }} className="text-sm text-gold">
            &larr; {t('worker.backToList')}
          </button>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-mono text-lg text-gold">{selected.serial}</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusColor(selected.status)}`}>
                {statusLabel(selected.status)}
              </span>
            </div>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>{t('worker.stoneType')}: {selected.stoneType}</p>
              <p>{t('worker.weight')}: {selected.handoffWeightCt} ct</p>
              <p>{t('worker.dateReceived')}: {new Date(selected.handoffDate).toLocaleDateString()}</p>
            </div>
          </div>

          {selected.status === 'awaiting_confirmation' && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground">{t('worker.confirmReceipt')}</h2>
              <p className="text-xs text-muted-foreground">{t('worker.confirmReceiptDesc')}</p>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('worker.yourWeight')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={receivedWeight}
                  onChange={(e) => setReceivedWeight(e.target.value)}
                  placeholder="ct"
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-3 text-lg text-foreground"
                />
              </div>
              <button
                onClick={handleConfirm}
                disabled={!receivedWeight || confirmMutation.isPending}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 py-3 text-sm font-medium text-[#0a0a0b] disabled:opacity-50"
              >
                <Check size={16} /> {t('worker.confirmReceiptBtn')}
              </button>
            </div>
          )}

          {selected.status === 'in_progress' && !showConfirmation && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground">{t('worker.submitForQC')}</h2>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('worker.finishedWeight')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={finishedWeight}
                  onChange={(e) => setFinishedWeight(e.target.value)}
                  placeholder="ct"
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-3 text-lg text-foreground"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('worker.shape')}</label>
                <select
                  value={shape}
                  onChange={(e) => setShape(e.target.value)}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-3 text-foreground"
                >
                  <option value="">{t('worker.selectShape')}</option>
                  {selected.shapes?.map((s: string) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setShowConfirmation(true)}
                disabled={!finishedWeight}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 py-3 text-sm font-medium text-[#0a0a0b] disabled:opacity-50"
              >
                {t('worker.submitQCBtn')}
              </button>
            </div>
          )}

          {selected.status === 'in_progress' && showConfirmation && (
            <div className="rounded-xl border border-gold/30 bg-gold/5 p-5 space-y-4">
              <div className="flex items-center gap-2 text-gold">
                <AlertTriangle size={18} />
                <h2 className="text-sm font-semibold">Confirm Submission</h2>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-foreground">
                  <span className="text-muted-foreground">Stone: </span>
                  <span className="font-mono text-gold">{selected.serial}</span>
                </p>
                <p className="text-foreground">
                  <span className="text-muted-foreground">Finished weight: </span>
                  <span className="font-mono">{finishedWeight} ct</span>
                </p>
                {shape && (
                  <p className="text-foreground">
                    <span className="text-muted-foreground">Shape: </span>
                    {shape}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                This cannot be undone. Please verify the weight is correct.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitMutation.isPending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gold px-4 py-3 text-sm font-medium text-[#0a0a0b] disabled:opacity-50"
                >
                  <Check size={16} />
                  {submitMutation.isPending ? 'Submitting...' : 'Confirm & Submit'}
                </button>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="rounded-lg border border-border px-4 py-3 text-sm text-foreground hover:bg-secondary"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {selected.status === 'submitted' && (
            <div className="rounded-xl border border-success/30 bg-success/5 p-5 text-center">
              <Check size={24} className="mx-auto mb-2 text-success" />
              <p className="text-sm text-success">{t('worker.alreadySubmitted')}</p>
            </div>
          )}
        </div>
      ) : (
        // Piece list
        <div className="space-y-3">
          {!pieces?.length ? (
            <div className="rounded-xl border border-border bg-card p-8 text-center">
              <p className="text-sm text-muted-foreground">{t('worker.noPieces')}</p>
            </div>
          ) : (
            pieces.map((p: any) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors active:bg-secondary"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-gold">{p.serial}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(p.status)}`}>
                    {statusLabel(p.status)}
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{p.handoffWeightCt} ct</span>
                  <span>{new Date(p.handoffDate).toLocaleDateString()}</span>
                  <span>{p.stoneType}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
